import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, X, ArrowRight } from "lucide-react";
import {
  getStoredReferralHandle,
  isReferralBannerDismissed,
  dismissReferralBanner,
  fetchReferrerCard,
  type ReferrerCard,
} from "@/lib/referral";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Subtle persistent reminder that a friend's ₹100 gift is active.
 * - Hidden on /ref/* (the landing already says it), /auth (it's nudged there),
 *   /payment* (the discount is visible on the order summary).
 * - Hidden once the user signs up + redeems (no stored ref) or dismisses it.
 */
export default function ReferralBanner() {
  const { user } = useAuth();
  const location = useLocation();
  const [handle, setHandle] = useState<string | null>(null);
  const [card, setCard] = useState<ReferrerCard | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  // Re-check on every route change
  useEffect(() => {
    const h = getStoredReferralHandle();
    setHandle(h);
    setDismissed(isReferralBannerDismissed());
  }, [location.pathname]);

  useEffect(() => {
    if (!handle) return;
    fetchReferrerCard(handle).then(setCard);
  }, [handle]);

  // Show after first paint to avoid layout shift
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 400);
    return () => clearTimeout(t);
  }, []);

  const pathHidden =
    location.pathname.startsWith("/ref/") ||
    location.pathname.startsWith("/auth") ||
    location.pathname.startsWith("/payment");

  if (!handle || dismissed || pathHidden || !visible) return null;

  const inviter = card?.firstName ?? "A friend";

  const handleDismiss = () => {
    dismissReferralBanner();
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ type: "spring", stiffness: 240, damping: 28 }}
        className="fixed z-40 left-3 right-3 sm:left-auto sm:right-4 sm:max-w-sm bottom-3 sm:bottom-4"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="rounded-2xl border border-primary/30 bg-background/95 backdrop-blur shadow-2xl p-3 sm:p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
            <Gift className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">
              {inviter}'s ₹100 gift is active
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {user
                ? "Auto-applies at checkout"
                : "Sign up to claim your discount"}
            </p>
          </div>
          {!user && (
            <Link
              to="/auth"
              className="inline-flex items-center gap-1 rounded-lg bg-primary text-primary-foreground text-xs font-semibold px-3 py-2 hover:opacity-90 transition-opacity shrink-0"
            >
              Claim <ArrowRight className="h-3 w-3" />
            </Link>
          )}
          <button
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 flex items-center justify-center shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
